import * as React from 'react';
import { Stack, Link, Separator } from '@fluentui/react';
import { getClassNames } from './HelpView.styles';
import { IHelpPanelLinkItem, IHelpViewItem } from './HelpView.types';
import * as Styled from './HelpView.styles';
import { helpConfigs, HelpLinks, MainTitle } from './Help.resources';
import { HelpPanelLinks } from './HelpPanelLinks';
const initialHelpViewClassNames = getClassNames();
const reportingWorkHoursChildText = (
    <Styled.HelpPanelUl>
        <li>
            Hourly/non-exempt employees can refer to{' '}
            <Link className={initialHelpViewClassNames.link} href={helpConfigs.tarToolGuide} target="TAR Tool Guide">
                <span aria-level={2}>HRWeb</span>
            </Link>{' '}
            for reporting/submitting their actual hours worked.
        </li>
        <li>Salaried employees only need to report their time off; no need to report hours worked.</li>
    </Styled.HelpPanelUl>
);

const timecardDueChildText = (
    <Styled.HelpPanelUl>
        <li>
            Hourly employees must submit a timecard for each pay period. For a list of timecard due dates click
            <Link className={initialHelpViewClassNames.link} href={helpConfigs.timecardDue} target="Timecard Due">
                <span aria-level={2}> here</span>
            </Link>
            .
        </li>
    </Styled.HelpPanelUl>
);

const outlookRemindersChildText = (
    <Styled.HelpPanelUl>
        <li>
            There are three reminder types available for download. Click{' '}
            <Link
                className={initialHelpViewClassNames.link}
                href={helpConfigs.outlookReminders}
                target="Outlook Reminders"
            >
                <span aria-level={2}> here</span>
            </Link>{' '}
            for instructions.​
        </li>
    </Styled.HelpPanelUl>
);

const vacationCarryoverChildText = (
    <Styled.HelpPanelUl>
        <li>
            Please review the
            <Link
                className={initialHelpViewClassNames.link}
                href={helpConfigs.nonCAVacationPolicy}
                target="Non-California Vacation Policy"
            >
                <span aria-level={2}> HRweb Non-California Vacation Policy </span>
            </Link>{' '}
            for details. The remainder is forfeited for non-California employees.​
        </li>
    </Styled.HelpPanelUl>
);

const employmentDetailChildText = (
    <Styled.HelpPanelUl>
        <li>
            Please visit{' '}
            <Link
                className={initialHelpViewClassNames.link}
                href={helpConfigs.employeeCentral}
                target="Employee Central"
            >
                <span aria-level={2}>Employee Central</span>
            </Link>
            {' or create a case with '}
            <Link className={initialHelpViewClassNames.link} href={helpConfigs.askHR} target="AskHR">
                <span aria-level={2}>AskHR</span>
            </Link>
            .
        </li>
    </Styled.HelpPanelUl>
);

const managerReportingGuideChildText = (
    <Styled.HelpPanelUl>
        <li>
            Please visit{' '}
            <Link
                className={initialHelpViewClassNames.link}
                href={helpConfigs.managerReportingGuide}
                target="TAR Simple Reporting Guide"
            >
                <span aria-level={2}>TAR Simple Reporting Guide</span>
            </Link>
            .
        </li>
    </Styled.HelpPanelUl>
);

export const timecardDue: IHelpViewItem = {
    title: 'When is my timecard due?',
    textChild: timecardDueChildText
};
export const reportingWorkHours: IHelpViewItem = {
    title: 'How do I report my hours worked?',
    textChild: reportingWorkHoursChildText
};

export const outlookReminders: IHelpViewItem = {
    title: 'Can I add an Outlook reminder to submit my timecards?​',
    textChild: outlookRemindersChildText
};

export const vacationCarryover: IHelpViewItem = {
    title: 'Will my vacation hours carryover to the following year?',
    textChild: vacationCarryoverChildText
};

export const employmentDetail: IHelpViewItem = {
    title: 'Where do I review or change my personal or employment details?',
    textChild: employmentDetailChildText
};

export const managerReportingGuide: IHelpViewItem = {
    title: 'How do I see if my team has reported vacation hours, including zero recorders?',
    textChild: managerReportingGuideChildText
};

export function HelpPanel(): React.ReactElement {
    const HelpSection = (props: { mainTitle: string; children?: React.ReactNode }): JSX.Element => {
        return (
            <Stack style={{ color: '#171717' }}>
                <div className={initialHelpViewClassNames.helpSectionTitle} style={{ fontSize: '18px' }}>
                    {props.mainTitle}
                </div>
                <div>{props.children}</div>
            </Stack>
        );
    };

    return (
        <Stack>
            <HelpSection mainTitle={MainTitle.helpTopics}>
            <div className={initialHelpViewClassNames.helpTextBreak} />
                <div id="managerReportingGuide">
                    <b>{managerReportingGuide.title}</b>
                    {managerReportingGuideChildText}
                </div>
                <div className={initialHelpViewClassNames.helpTextBreak} />
                <div id="reportingWorkHours">
                    <b>{reportingWorkHours.title}</b>
                    {reportingWorkHoursChildText}
                </div>
                <div className={initialHelpViewClassNames.helpTextBreak} />
                <div id="timecardDue">
                    <b>{timecardDue.title}</b>
                    {timecardDueChildText}
                </div>
                <div className={initialHelpViewClassNames.helpTextBreak} />
                <div id="outlookReminder">
                    <b>{outlookReminders.title}</b>
                    {outlookRemindersChildText}
                </div>
                <div className={initialHelpViewClassNames.helpTextBreak} />
                <div id="vacationCarryover">
                    <b>{vacationCarryover.title}</b>
                    {vacationCarryoverChildText}
                </div>
                <div className={initialHelpViewClassNames.helpTextBreak} />
                <div id="employmentDetail">
                    <b>{employmentDetail.title}</b>
                    {employmentDetailChildText}
                </div>
            </HelpSection>
            <Separator className={initialHelpViewClassNames.helpSeparator} />
            <HelpSection mainTitle={MainTitle.other}>
                {HelpLinks.map((value: IHelpPanelLinkItem, index: number) => {
                    return (
                        <HelpPanelLinks
                            key={index}
                            hrefLink={value.hrefLink}
                            target={value.target}
                            id={value.id}
                            text={value.text}
                        />
                    );
                })}
            </HelpSection>
        </Stack>
    );
}
